import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authApi, settingsApi, accountDeletionApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, Pencil, X, User, Mail, MapPin, Users as UsersIcon, Phone, Calendar, AlertCircle, Send, Award, Trash2, ShieldAlert } from 'lucide-react';
import { MemberHeader } from '../common/MemberHeader';
import { MemberBreadcrumb } from '../common/MemberBreadcrumb';
import { GdprAndDevicesSection } from '../common/GdprAndDevicesSection';

/**
 * Standalone page for viewing and editing personal contact data.
 * Read-only by default; user can toggle edit mode per section or edit everything
 * at once via the main "Bearbeiten"-Button.
 */
export default function ContactDetailsPage() {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        salutation: '', academic_title: '', first_name: '', last_name: '',
        phone: '', birth_date: '', street: '', postal_code: '', city: '', club: '',
        qualifications: [], qualification_licenses: [],
    });
    const [qualificationOptions, setQualificationOptions] = useState([]);
    const [licenseTypeOptions, setLicenseTypeOptions] = useState([]);

    // Email-change flow (Option B: verification link to new address)
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [emailSubmitting, setEmailSubmitting] = useState(false);
    const [pendingEmailChange, setPendingEmailChange] = useState(null);

    // DSGVO — Konto-Löschung beantragen
    const [deletionRequest, setDeletionRequest] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletionReason, setDeletionReason] = useState('');
    const [deletionSubmitting, setDeletionSubmitting] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/anmelden');
            return;
        }
        resetForm(user);
        loadPendingEmailChange();
        loadDeletionRequest();
        // Einstellungs-Optionen (Qualifikation + Lizenztypen) aus Admin-Settings
        (async () => {
            try {
                const [qRes, ltRes] = await Promise.all([
                    settingsApi.getQualifications(),
                    settingsApi.getLicenseTypes(),
                ]);
                setQualificationOptions(qRes.data.options || []);
                setLicenseTypeOptions(ltRes.data.options || []);
            } catch { /* options stay empty */ }
        })();
    }, [user, navigate]);

    const loadPendingEmailChange = async () => {
        try {
            const res = await authApi.getEmailChangeStatus();
            setPendingEmailChange(res.data.pending);
        } catch { setPendingEmailChange(null); }
    };

    const loadDeletionRequest = async () => {
        try {
            const res = await accountDeletionApi.getMyRequest();
            setDeletionRequest(res.data || null);
        } catch { setDeletionRequest(null); }
    };

    const handleRequestDeletion = async () => {
        setDeletionSubmitting(true);
        try {
            await accountDeletionApi.requestDeletion(deletionReason.trim() || null);
            toast.success('Löschantrag eingereicht. Wir melden uns nach Prüfung.');
            setDeleteDialogOpen(false);
            setDeletionReason('');
            loadDeletionRequest();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Antrag konnte nicht gestellt werden');
        } finally {
            setDeletionSubmitting(false);
        }
    };

    const handleWithdrawDeletion = async () => {
        if (!window.confirm('Löschantrag wirklich zurückziehen? Ihr Konto bleibt dann bestehen.')) return;
        try {
            await accountDeletionApi.withdrawMyRequest();
            toast.success('Antrag zurückgezogen');
            setDeletionRequest(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    const handleRequestEmailChange = async () => {
        const trimmed = (newEmailInput || '').trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error('Bitte eine gültige E-Mail-Adresse angeben');
            return;
        }
        if (trimmed.toLowerCase() === (user.email || '').toLowerCase()) {
            toast.error('Die neue Adresse entspricht der aktuellen');
            return;
        }
        setEmailSubmitting(true);
        try {
            await authApi.requestEmailChange(trimmed);
            toast.success('Bestätigungs-Link an die neue Adresse versendet. Bitte prüfen Sie Ihr Postfach.');
            setEmailDialogOpen(false);
            setNewEmailInput('');
            loadPendingEmailChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Versand');
        } finally {
            setEmailSubmitting(false);
        }
    };

    const handleCancelEmailChange = async () => {
        try {
            await authApi.cancelEmailChange();
            toast.success('Änderungs-Antrag verworfen');
            setPendingEmailChange(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    const resetForm = (u) => {
        // Legacy: qualifications can be string OR array. Always work with array.
        let qArr = u?.qualifications;
        if (Array.isArray(qArr)) qArr = qArr.filter(Boolean);
        else if (typeof qArr === 'string' && qArr.trim()) qArr = [qArr.trim()];
        else qArr = [];
        let qLic = Array.isArray(u?.qualification_licenses) ? [...u.qualification_licenses] : [];
        if (u?.is_bgpr_herzgruppenleiter && !qLic.includes('BGPR-Herzgruppenleiter')) {
            qLic.push('BGPR-Herzgruppenleiter');
        }
        setForm({
            salutation: u?.salutation || '',
            academic_title: u?.academic_title || '',
            first_name: u?.first_name || '',
            last_name: u?.last_name || '',
            phone: u?.phone || '',
            birth_date: u?.birth_date || '',
            street: u?.street || '',
            postal_code: u?.postal_code || '',
            city: u?.city || '',
            club: u?.club || '',
            qualifications: qArr,
            qualification_licenses: qLic,
        });
    };

    const handleCancel = () => {
        resetForm(user);
        setEditing(false);
    };

    const handleSave = async (e) => {
        e?.preventDefault?.();
        if (!form.first_name || !form.last_name || !form.birth_date || !form.street || !form.postal_code || !form.city) {
            toast.error('Bitte alle Pflichtfelder ausfüllen');
            return;
        }
        setSaving(true);
        try {
            const qLic = Array.isArray(form.qualification_licenses) ? form.qualification_licenses : [];
            const payload = {
                ...form,
                qualifications: Array.isArray(form.qualifications) ? form.qualifications.filter(Boolean) : [],
                qualification_licenses: qLic,
                is_bgpr_herzgruppenleiter: qLic.includes('BGPR-Herzgruppenleiter'),
            };
            await authApi.updateProfile(payload);
            toast.success('Ihre Kontaktdaten wurden gespeichert');
            await checkAuth();
            setEditing(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const displayName = () => {
        const parts = [
            form.salutation,
            form.academic_title,
            form.first_name,
            form.last_name,
        ].filter(Boolean);
        return parts.length ? parts.join(' ') : '–';
    };

    const formatDate = (iso) => {
        if (!iso) return '–';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return iso;
        }
    };

    if (!user) return null;

    const Row = ({ label, icon: Icon, children }) => (
        <div className="grid grid-cols-[auto_1fr] items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide pt-0.5 min-w-[120px]">
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {label}
            </div>
            <div className="text-sm text-gray-900">{children}</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <MemberHeader />
            <MemberBreadcrumb />

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Meine Kontaktdaten</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Diese Angaben werden für Lehrgangsbescheinigungen, Lizenzen und Rechnungen verwendet.
                            Bitte halten Sie sie aktuell (z.&nbsp;B. bei Namensänderung oder Umzug).
                        </p>
                    </div>
                    {!editing && (
                        <Button onClick={() => setEditing(true)} data-testid="edit-contact-details-button">
                            <Pencil className="h-4 w-4 mr-1.5" /> Bearbeiten
                        </Button>
                    )}
                </div>

                {editing ? (
                    /* ===================== EDIT MODE ===================== */
                    <form onSubmit={handleSave} className="space-y-4" data-testid="contact-details-form">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <User className="h-4 w-4" /> Persönliche Daten
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor="salutation" className="text-xs">Anrede</Label>
                                    <Select value={form.salutation} onValueChange={(v) => setForm({ ...form, salutation: v })}>
                                        <SelectTrigger id="salutation" data-testid="cd-salutation"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Herr">Herr</SelectItem>
                                            <SelectItem value="Frau">Frau</SelectItem>
                                            <SelectItem value="Divers">Divers</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="academic_title" className="text-xs">Titel (optional)</Label>
                                    <Input id="academic_title" placeholder="Dr., Prof., …" value={form.academic_title} onChange={(e) => setForm({ ...form, academic_title: e.target.value })} data-testid="cd-title" />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="first_name" className="text-xs">Vorname <span className="text-red-500">*</span></Label>
                                    <Input id="first_name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="cd-first-name" />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="last_name" className="text-xs">Nachname <span className="text-red-500">*</span></Label>
                                    <Input id="last_name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="cd-last-name" />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <Label htmlFor="birth_date" className="text-xs">Geburtsdatum <span className="text-red-500">*</span></Label>
                                    <Input id="birth_date" type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="cd-birth-date" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> Kontakt
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="space-y-1">
                                    <Label htmlFor="email" className="text-xs">E-Mail</Label>
                                    <Input id="email" type="email" value={user.email} disabled data-testid="cd-email" />
                                    <p className="text-xs text-gray-400">Zum Ändern bitte die Geschäftsstelle kontaktieren.</p>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="phone" className="text-xs">Telefon (optional)</Label>
                                    <Input id="phone" placeholder="+49 30 123 456" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cd-phone" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <MapPin className="h-4 w-4" /> Anschrift
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="space-y-1">
                                    <Label htmlFor="street" className="text-xs">Straße und Hausnummer <span className="text-red-500">*</span></Label>
                                    <Input id="street" placeholder="Musterstraße 1" required value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} data-testid="cd-street" />
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="postal_code" className="text-xs">PLZ <span className="text-red-500">*</span></Label>
                                        <Input id="postal_code" placeholder="10115" required value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="cd-postal-code" />
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        <Label htmlFor="city" className="text-xs">Ort <span className="text-red-500">*</span></Label>
                                        <Input id="city" placeholder="Berlin" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="cd-city" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <UsersIcon className="h-4 w-4" /> Verein / Organisation
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-1">
                                    <Label htmlFor="club" className="text-xs">Verein (optional)</Label>
                                    <Input id="club" placeholder="z.B. Herzsportgruppe Mitte e.V." value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} data-testid="cd-club" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Award className="h-4 w-4" /> Qualifikation & Lizenzen
                                </CardTitle>
                                <CardDescription>
                                    Wählen Sie aus, welche beruflichen Vorqualifikationen und
                                    Lizenzen Sie besitzen. Mehrfachauswahl möglich.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Berufliche Vorqualifikation</Label>
                                    {qualificationOptions.length === 0 ? (
                                        <p className="text-sm text-gray-500">Derzeit keine Qualifikationen verfügbar.</p>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cd-qualifications">
                                            {qualificationOptions.map((q) => {
                                                const selected = (form.qualifications || []).includes(q);
                                                return (
                                                    <label key={q} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-primary/5 border-primary/40' : 'bg-white hover:bg-gray-50'}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={(e) => {
                                                                const prev = form.qualifications || [];
                                                                const next = e.target.checked ? [...prev, q] : prev.filter((x) => x !== q);
                                                                setForm({ ...form, qualifications: next });
                                                            }}
                                                            className="w-5 h-5 rounded text-primary"
                                                            data-testid={`cd-qualification-${q}`}
                                                        />
                                                        <span className="text-sm font-medium">{q}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2 pt-4 border-t">
                                    <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Lizenz-/Qualifikationstypen</Label>
                                    {licenseTypeOptions.length === 0 ? (
                                        <p className="text-sm text-gray-500">Derzeit keine Lizenztypen verfügbar.</p>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cd-license-types">
                                            {licenseTypeOptions.map((lt) => {
                                                const selected = (form.qualification_licenses || []).includes(lt);
                                                return (
                                                    <label key={lt} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-primary/5 border-primary/40' : 'bg-white hover:bg-gray-50'}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={(e) => {
                                                                const prev = form.qualification_licenses || [];
                                                                const next = e.target.checked ? [...prev, lt] : prev.filter((x) => x !== lt);
                                                                setForm({ ...form, qualification_licenses: next });
                                                            }}
                                                            className="w-5 h-5 rounded text-primary"
                                                            data-testid={`cd-license-type-${lt}`}
                                                        />
                                                        <span className="text-sm font-medium">{lt}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        </div>

                        <div className="flex justify-end gap-2 sticky bottom-4 bg-gray-50/80 backdrop-blur-sm py-3 -mx-4 px-4 rounded-lg">
                            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
                                <X className="h-4 w-4 mr-1.5" /> Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="cd-save-button">
                                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                                Speichern
                            </Button>
                        </div>
                    </form>
                ) : (
                    /* ===================== VIEW MODE ===================== */
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="contact-details-view">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <User className="h-4 w-4" /> Persönliche Daten
                                </CardTitle>
                                <CardDescription>Name und Geburtsdatum</CardDescription>
                            </CardHeader>
                            <CardContent className="divide-y divide-gray-100">
                                <Row label="Name" icon={User}>
                                    <span className="font-medium">{displayName()}</span>
                                </Row>
                                <Row label="Geburtsdatum" icon={Calendar}>
                                    {formatDate(form.birth_date)}
                                </Row>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> Kontakt
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Row label="E-Mail" icon={Mail}>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{user.email}</span>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs"
                                            onClick={() => { setNewEmailInput(''); setEmailDialogOpen(true); }}
                                            data-testid="change-email-btn"
                                        >
                                            <Pencil className="h-3 w-3 mr-1" /> E-Mail ändern
                                        </Button>
                                    </div>
                                    {pendingEmailChange && (
                                        <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="pending-email-banner">
                                            <div className="flex items-start gap-2">
                                                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <p>
                                                        Änderungs-Antrag auf <strong>{pendingEmailChange.new_email}</strong> offen.
                                                        Bitte prüfen Sie das Postfach und klicken Sie auf den Bestätigungs-Link.
                                                    </p>
                                                    <button type="button" className="underline mt-1 text-amber-900 hover:text-amber-950" onClick={handleCancelEmailChange} data-testid="cancel-email-change-btn">
                                                        Antrag verwerfen
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Row>
                                <Row label="Telefon" icon={Phone}>{form.phone || <span className="text-gray-400">nicht angegeben</span>}</Row>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <MapPin className="h-4 w-4" /> Anschrift
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Row label="Straße" icon={MapPin}>{form.street || <span className="text-gray-400">–</span>}</Row>
                                <Row label="PLZ / Ort" icon={MapPin}>
                                    {(form.postal_code || form.city)
                                        ? `${form.postal_code || ''} ${form.city || ''}`.trim()
                                        : <span className="text-gray-400">–</span>}
                                </Row>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <UsersIcon className="h-4 w-4" /> Verein / Organisation
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Row label="Verein" icon={UsersIcon}>
                                    {form.club || <span className="text-gray-400">nicht angegeben</span>}
                                </Row>
                            </CardContent>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Award className="h-4 w-4" /> Qualifikation & Lizenzen
                                </CardTitle>
                                <CardDescription>Ihre beruflichen Vorqualifikationen und Lizenzen</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide mb-2">Berufliche Vorqualifikation</p>
                                    {form.qualifications?.length ? (
                                        <div className="flex flex-wrap gap-2" data-testid="cd-qualification-badges">
                                            {form.qualifications.map((q) => (
                                                <span key={q} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                                    <Award className="h-3.5 w-3.5" />{q}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400">Keine Qualifikation angegeben</p>
                                    )}
                                </div>
                                <div className="pt-3 border-t">
                                    <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide mb-2">Lizenz-/Qualifikationstypen</p>
                                    {form.qualification_licenses?.length ? (
                                        <div className="flex flex-wrap gap-2" data-testid="cd-license-badges">
                                            {form.qualification_licenses.map((lt) => (
                                                <span key={lt} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                                                    <Award className="h-3.5 w-3.5" />{lt}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400">Keine Lizenz angegeben</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* DSGVO Art. 15 Datenauskunft + Meine vertrauten Geräte */}
                <GdprAndDevicesSection />

                {/* DSGVO — Konto-Löschung beantragen */}
                <Card className="mt-6 border-rose-200 bg-rose-50/30" data-testid="account-deletion-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2 text-rose-900">
                            <ShieldAlert className="h-4 w-4" /> Konto löschen (DSGVO)
                        </CardTitle>
                        <CardDescription className="text-rose-800/80">
                            Sie haben jederzeit das Recht auf Löschung Ihrer Daten (Art. 17 DSGVO).
                            Stellen Sie hier einen Antrag — die Geschäftsstelle prüft und führt
                            die Löschung durch. Lehrgangsbescheinigungen und Lizenz-PDFs in unserem
                            Cloud-Speicher werden dabei mit entfernt.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {deletionRequest && deletionRequest.status === 'pending' && (
                            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2">
                                <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" /> Antrag in Bearbeitung
                                </p>
                                <p className="text-xs text-amber-900/80">
                                    Eingereicht am {new Date(deletionRequest.requested_at).toLocaleString('de-DE')}.
                                    Sie erhalten eine Rückmeldung, sobald die Geschäftsstelle Ihren Antrag
                                    geprüft hat. Bis dahin können Sie den Antrag jederzeit zurückziehen.
                                </p>
                                {deletionRequest.reason && (
                                    <p className="text-xs text-amber-900/80 italic">
                                        Begründung: „{deletionRequest.reason}"
                                    </p>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-amber-400 text-amber-900"
                                    onClick={handleWithdrawDeletion}
                                    data-testid="withdraw-deletion-btn"
                                >
                                    <X className="h-3.5 w-3.5 mr-1.5" /> Antrag zurückziehen
                                </Button>
                            </div>
                        )}
                        {deletionRequest && deletionRequest.status === 'rejected' && (
                            <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 space-y-2 mb-3">
                                <p className="text-sm font-semibold text-rose-900">
                                    Letzter Antrag wurde abgelehnt
                                </p>
                                <p className="text-xs text-rose-900/80">
                                    Geprüft am {deletionRequest.reviewed_at ? new Date(deletionRequest.reviewed_at).toLocaleString('de-DE') : '–'}.
                                </p>
                                {deletionRequest.review_reason && (
                                    <p className="text-xs text-rose-900/80 italic">
                                        Begründung: „{deletionRequest.review_reason}"
                                    </p>
                                )}
                            </div>
                        )}
                        {(!deletionRequest || deletionRequest.status !== 'pending') && (
                            <Button
                                variant="outline"
                                className="border-rose-400 text-rose-900 hover:bg-rose-100"
                                onClick={() => setDeleteDialogOpen(true)}
                                data-testid="request-deletion-btn"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" /> Konto-Löschung beantragen
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </main>

            {/* E-Mail ändern — Dialog (Option B: Verifizierung über neue Adresse) */}
            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                <DialogContent data-testid="email-change-dialog">
                    <DialogHeader>
                        <DialogTitle>E-Mail-Adresse ändern</DialogTitle>
                        <DialogDescription>
                            Ihre E-Mail-Adresse ist gleichzeitig Ihr Login. Aus Sicherheitsgründen
                            senden wir einen Bestätigungs-Link an die neue Adresse. Erst nach
                            Klick auf den Link wird die Änderung aktiv.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-gray-50 border text-xs">
                            <p className="text-gray-600">Aktuelle Adresse:</p>
                            <p className="font-medium text-gray-900">{user.email}</p>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Neue E-Mail-Adresse <span className="text-red-500">*</span></Label>
                            <Input
                                type="email"
                                value={newEmailInput}
                                onChange={(e) => setNewEmailInput(e.target.value)}
                                placeholder="neue.adresse@example.com"
                                data-testid="new-email-input"
                                autoFocus
                            />
                        </div>
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                            <strong>Wichtig:</strong> Sobald Sie auf den Bestätigungs-Link in der neuen
                            Mailbox klicken, werden alle aktiven Sitzungen abgemeldet. Sie müssen sich
                            anschließend mit der neuen Adresse erneut anmelden. Ihr Passwort bleibt
                            unverändert. Eine Info-Mail geht zusätzlich an Ihre aktuelle Adresse.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Abbrechen</Button>
                        <Button onClick={handleRequestEmailChange} disabled={emailSubmitting} data-testid="email-change-submit">
                            {emailSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            Bestätigungs-Link senden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* DSGVO — Konto-Löschung beantragen */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent data-testid="account-deletion-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-900">
                            <ShieldAlert className="h-5 w-5" /> Konto-Löschung beantragen
                        </DialogTitle>
                        <DialogDescription>
                            Wenn Ihr Antrag genehmigt wird, werden alle Ihre Daten (Lizenzen,
                            Buchungen, Verlängerungsanträge, Bescheinigungen, Quittungen, Konto)
                            unwiderruflich gelöscht. PDF-Dateien in unserem Cloud-Speicher werden
                            in den Papierkorb verschoben.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                            <strong>Bitte beachten:</strong> Bereits ausgestellte Lehrgangsbescheinigungen
                            und Lizenznachweise sind nach der Löschung nicht mehr abrufbar — bitte vorher
                            herunterladen und sichern, falls Sie sie behalten möchten.
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="deletion-reason" className="text-xs">Begründung (optional)</Label>
                            <Textarea
                                id="deletion-reason"
                                value={deletionReason}
                                onChange={(e) => setDeletionReason(e.target.value)}
                                placeholder="z. B. Nicht mehr aktiv als Übungsleiter, Wechsel in einen anderen Verein, …"
                                rows={3}
                                data-testid="deletion-reason-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deletionSubmitting}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleRequestDeletion}
                            disabled={deletionSubmitting}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="confirm-deletion-request"
                        >
                            {deletionSubmitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                            Löschung beantragen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
